import type {
  CustomerFundSwitchFund,
  FundSwitchFund,
  FundSwitchPolicy,
  FundSwitchSource,
  InsuranceManagementDetail,
  ListedInsurance,
  PolicyCalculation,
  PolicyValues,
  SplitPolicies,
} from "../../types";
import type { Insurance } from "../../hooks/api/useGetCalculationById";
import { knownCapital } from "../../utils/capital";

const TRADITIONAL = "TRAD";

const toFund = (fund: CustomerFundSwitchFund): FundSwitchFund => ({
  name: fund.name,
  isin: fund.isin || undefined,
  shareOfTotal: fund.percentage,
});

const isTraditional = (detail: InsuranceManagementDetail) =>
  String(detail.managementType).toUpperCase() === TRADITIONAL;

const sumValues = (details: InsuranceManagementDetail[]) =>
  details.reduce((sum, detail) => sum + (Number(detail.value) || 0), 0);

function toPolicyValues(entry: Insurance | undefined): PolicyValues {
  const details = entry?.managementDetails;
  if (!Array.isArray(details)) return {};

  return {
    totalValue: Number(entry?.insuranceCapital) || 0,
    fundPartValue: sumValues(details.filter((d) => !isTraditional(d))),
    traditionalValue: sumValues(details.filter(isTraditional)),
  };
}

// The fund switches the calculation reports, in the shape the cards render. A policy whose fund
// part holds nothing has nothing to switch, so it is left out and lands under Övriga instead.
export function toFundSwitchPolicies(
  calculation: FundSwitchSource,
): FundSwitchPolicy[] {
  const listed = new Map(
    (calculation?.totalInsuranceList ?? []).map((policy) => [
      policy.insuranceIdentifier,
      policy,
    ]),
  );

  return (calculation?.fundSwitches ?? [])
    .filter((policy) => !!policy?.insuranceIdentifier)
    .map((policy) => ({
      insuranceIdentifier: policy.insuranceIdentifier,
      insuranceCompany: policy.insuranceCompany,
      logotype: policy.logotype,
      owner: policy.owner?.legalName
        ? { legalName: policy.owner.legalName }
        : undefined,
      currentFunds: (policy.currentFunds ?? []).map(toFund),
      recommendedFunds: (policy.advicedFunds ?? []).map(toFund),
      deltaFirstMonthPension:
        typeof policy.deltaFirstMonthPension === "number"
          ? policy.deltaFirstMonthPension
          : undefined,
      ...toPolicyValues(listed.get(policy.insuranceIdentifier)),
    }))
    .filter(
      (policy) =>
        policy.fundPartValue === undefined || policy.fundPartValue > 0,
    );
}

export function withCapital<T extends ListedInsurance>(
  rows: T[],
  policyList: Insurance[] | undefined,
): T[] {
  const listed = new Map(
    (policyList ?? []).map((policy) => [policy.insuranceIdentifier, policy]),
  );

  return rows.map((row) => {
    if (knownCapital(row.insuranceCapital) !== undefined) return row;
    const entry = listed.get(row.insuranceIdentifier);
    return entry && knownCapital(entry.insuranceCapital) !== undefined
      ? { ...row, insuranceCapital: entry.insuranceCapital }
      : row;
  });
}

// Fund switches with the largest expected gain first.
export function sortByImpact(
  fundSwitches: FundSwitchPolicy[],
): FundSwitchPolicy[] {
  const impact = (policy: FundSwitchPolicy) =>
    typeof policy.deltaFirstMonthPension === "number" &&
    Number.isFinite(policy.deltaFirstMonthPension)
      ? policy.deltaFirstMonthPension
      : Number.NEGATIVE_INFINITY;

  return [...fundSwitches].sort((a, b) => impact(b) - impact(a));
}

export function splitPolicies(
  calculation: PolicyCalculation,
  adviceRows: ListedInsurance[] | null | undefined,
): SplitPolicies {
  const fromCalculation =
    calculation?.transferAllocation?.insurancesForTransfer
      ?.insuranceTransferList ?? [];

  const seen = new Set<string>();
  const transfers: ListedInsurance[] = [];

  [...(adviceRows ?? []), ...fromCalculation].forEach((policy) => {
    if (!policy?.insuranceIdentifier || seen.has(policy.insuranceIdentifier)) {
      return;
    }
    seen.add(policy.insuranceIdentifier);
    transfers.push(policy);
  });

  // A policy with a recommended fund switch is shown under Fondbyten; the same list the cards
  // render, so a policy the cards leave out is listed here.
  toFundSwitchPolicies(calculation).forEach((policy) => {
    seen.add(policy.insuranceIdentifier);
  });

  const others = (calculation?.totalInsuranceList ?? []).filter(
    (policy) => !seen.has(policy.insuranceIdentifier),
  );

  return {
    transfers: withCapital(transfers, calculation?.totalInsuranceList),
    others,
  };
}
